/**
 * 使用者詳情（權限管理 — 切片 3）。
 *
 * 對應 BE GET /admin/users/:id、GET /admin/roles、PUT /admin/users/:id/roles。
 *
 * 功能：
 *   - 使用者 meta 檢視
 *   - 角色指派（覆蓋式）—— 勾選使用者應擁有之角色，存回 BE
 *   - 2FA 連動提示：指派 require_2fa 角色給尚未綁定 2FA 之使用者時提醒
 *   - anti-lockout：BE 阻擋移除系統最後一個能管理 RBAC 的使用者（409）
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { Button } from '../components/Button';
import { Pill } from '../components/Pill';
import {
  getUser,
  listRoles,
  setUserRoles,
  setUserStatus,
  type RbacRoleListItem,
  type SetUserStatusInput,
} from '../lib/api-client';

function formatDateTime(iso: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('zh-TW');
  } catch {
    return iso;
  }
}

export function UserDetailPage(): JSX.Element {
  const { id = '' } = useParams<{ id: string }>();
  const queryClient = useQueryClient();

  const [editing, setEditing] = useState(false);
  /** 編輯模式下勾選的角色 id 集合 */
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const userQuery = useQuery({
    queryKey: ['admin.users', id],
    queryFn: () => getUser(id),
    enabled: id.length > 0,
  });

  const rolesQuery = useQuery({
    queryKey: ['admin.roles'],
    queryFn: listRoles,
  });

  const invalidateUser = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['admin.users'] });
    void queryClient.invalidateQueries({ queryKey: ['admin.users', id] });
  };

  const mutation = useMutation({
    mutationFn: (roleIds: string[]) => setUserRoles(id, roleIds),
    onSuccess: () => {
      invalidateUser();
      setEditing(false);
    },
  });

  const statusMutation = useMutation({
    mutationFn: (input: SetUserStatusInput) => setUserStatus(id, input),
    onSuccess: invalidateUser,
  });

  const user = userQuery.data;
  const allRoles: RbacRoleListItem[] = rolesQuery.data?.items ?? [];

  /** 編輯中：勾選之角色裡是否有 require_2fa 者（且使用者尚未綁 2FA）→ 顯示提示 */
  const twoFactorGap = useMemo<boolean>(() => {
    if (!user || user.two_factor_enabled) return false;
    return allRoles.some((r) => selected.has(r.id) && r.require_2fa);
  }, [user, allRoles, selected]);

  const enterEdit = (): void => {
    setSelected(new Set((user?.roles ?? []).map((r) => r.id)));
    mutation.reset();
    setEditing(true);
  };

  const toggle = (roleId: string): void => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(roleId)) next.delete(roleId);
      else next.add(roleId);
      return next;
    });
  };

  return (
    <div>
      <div className="page-head">
        <h1 className="page-head__title">使用者詳情</h1>
        <div className="page-head__actions">
          <Link to="/admin/users">
            <Button variant="ghost">← 返回使用者列表</Button>
          </Link>
          {!editing && (
            <>
              <Button
                variant="secondary"
                onClick={() => userQuery.refetch()}
                disabled={userQuery.isFetching}
              >
                {userQuery.isFetching ? '更新中…' : '重新整理'}
              </Button>
              <Button variant="primary" onClick={enterEdit} disabled={!user}>
                編輯角色
              </Button>
            </>
          )}
          {editing && (
            <>
              <Button
                variant="secondary"
                onClick={() => setEditing(false)}
                disabled={mutation.isPending}
              >
                取消
              </Button>
              <Button
                variant="primary"
                onClick={() => mutation.mutate([...selected])}
                disabled={mutation.isPending}
              >
                {mutation.isPending ? '儲存中…' : `儲存（${selected.size} 個角色）`}
              </Button>
            </>
          )}
        </div>
      </div>

      {userQuery.isLoading && <div>載入中…</div>}
      {userQuery.error && <div className="error-banner">{(userQuery.error as Error).message}</div>}
      {mutation.error && (
        <div className="error-banner">
          儲存失敗：{(mutation.error as Error).message}
          <div style={{ marginTop: 4, fontSize: 12 }}>
            提醒：指派角色需 admin.rbac_manage 權限（僅 SuperAdmin 預設持有）。
          </div>
        </div>
      )}

      {user && (
        <>
          <div className="card" style={{ marginBottom: 16 }}>
            <dl
              style={{
                display: 'grid',
                gridTemplateColumns: 'auto 1fr',
                gap: '6px 16px',
                margin: 0,
              }}
            >
              <dt style={{ color: 'var(--c-text-muted)', fontSize: 13 }}>Email</dt>
              <dd style={{ margin: 0, fontFamily: 'monospace', fontSize: 13 }}>{user.email}</dd>
              <dt style={{ color: 'var(--c-text-muted)', fontSize: 13 }}>姓名</dt>
              <dd style={{ margin: 0 }}>{user.full_name ?? '—'}</dd>
              <dt style={{ color: 'var(--c-text-muted)', fontSize: 13 }}>帳號狀態</dt>
              <dd style={{ margin: 0 }}>
                {user.account_status === 'active' ? (
                  <Pill tone="success">啟用</Pill>
                ) : (
                  <Pill tone="danger">{user.account_status}</Pill>
                )}
              </dd>
              <dt style={{ color: 'var(--c-text-muted)', fontSize: 13 }}>2FA</dt>
              <dd style={{ margin: 0 }}>
                {user.two_factor_enabled ? (
                  <Pill tone="success">已綁定</Pill>
                ) : (
                  <Pill tone="neutral">未綁定</Pill>
                )}
              </dd>
              <dt style={{ color: 'var(--c-text-muted)', fontSize: 13 }}>最後登入</dt>
              <dd style={{ margin: 0, fontSize: 13 }}>{formatDateTime(user.last_login_at)}</dd>
              <dt style={{ color: 'var(--c-text-muted)', fontSize: 13 }}>建立時間</dt>
              <dd style={{ margin: 0, fontSize: 13 }}>{formatDateTime(user.created_at)}</dd>
            </dl>
          </div>

          {!editing && (
            <div className="card" style={{ marginBottom: 16 }}>
              <h2 style={{ fontSize: 15, margin: '0 0 8px 0' }}>帳號狀態管理</h2>
              <p style={{ fontSize: 13, color: 'var(--c-text-muted)', margin: '0 0 8px 0' }}>
                目前狀態：<strong>{user.account_status}</strong>
                {user.locked_until
                  ? `；帳號鎖定至 ${formatDateTime(user.locked_until)}`
                  : '；未鎖定'}
              </p>
              {statusMutation.error && (
                <div className="error-banner" style={{ marginBottom: 8 }}>
                  操作失敗：{(statusMutation.error as Error).message}
                </div>
              )}
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <Button
                  variant="secondary"
                  disabled={statusMutation.isPending || user.account_status === 'active'}
                  onClick={() => statusMutation.mutate({ account_status: 'active' })}
                >
                  設為啟用
                </Button>
                <Button
                  variant="secondary"
                  disabled={statusMutation.isPending || user.account_status === 'suspended'}
                  onClick={() => {
                    if (window.confirm('確定停權此帳號？停權後使用者無法登入。'))
                      statusMutation.mutate({ account_status: 'suspended' });
                  }}
                >
                  停權
                </Button>
                <Button
                  variant="danger"
                  disabled={statusMutation.isPending || user.account_status === 'deactivated'}
                  onClick={() => {
                    if (window.confirm('確定停用此帳號？停用為長期關閉。'))
                      statusMutation.mutate({ account_status: 'deactivated' });
                  }}
                >
                  停用
                </Button>
                {user.locked_until && (
                  <Button
                    variant="primary"
                    disabled={statusMutation.isPending}
                    onClick={() => statusMutation.mutate({ unlock: true })}
                  >
                    解除鎖定
                  </Button>
                )}
              </div>
            </div>
          )}

          {editing ? (
            <>
              <h2 style={{ fontSize: 15, margin: '0 0 6px 0' }}>指派角色</h2>
              <p className="page-intro" style={{ marginTop: 0 }}>
                勾選此使用者應擁有之角色；儲存為覆蓋式。角色變更於該使用者
                <strong>下次登入</strong>後生效（JWT scope 於登入時烙印）。
              </p>
              {twoFactorGap && (
                <div className="error-banner" style={{ background: '#fef3c7', color: '#92400e' }}>
                  ⚠️ 已勾選強制 2FA 之角色，但此使用者尚未綁定 2FA —— 其下次登入須先完成 2FA
                  綁定方可進入後台。
                </div>
              )}
              {rolesQuery.isLoading && <div>載入角色清單…</div>}
              {rolesQuery.error && (
                <div className="error-banner">{(rolesQuery.error as Error).message}</div>
              )}
              <div className="table-card">
                <table>
                  <thead>
                    <tr>
                      <th style={{ width: 60 }}>選取</th>
                      <th>角色 key</th>
                      <th>顯示名稱</th>
                      <th>2FA</th>
                    </tr>
                  </thead>
                  <tbody>
                    {allRoles.map((r) => (
                      <tr key={r.id}>
                        <td>
                          <input
                            type="checkbox"
                            checked={selected.has(r.id)}
                            onChange={() => toggle(r.id)}
                            aria-label={r.key}
                          />
                        </td>
                        <td style={{ fontFamily: 'monospace', fontSize: 12 }}>{r.key}</td>
                        <td>{r.display_name}</td>
                        <td>
                          {r.require_2fa ? (
                            <Pill tone="warning">強制 2FA</Pill>
                          ) : (
                            <Pill tone="neutral">免</Pill>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            <>
              <h2 style={{ fontSize: 15, margin: '0 0 6px 0' }}>
                已掛角色
                <span style={{ color: 'var(--c-text-muted)', fontWeight: 400, marginLeft: 8 }}>
                  （{user.roles.length} 個）
                </span>
              </h2>
              {user.roles.length === 0 ? (
                <div className="table-card">
                  <table>
                    <tbody>
                      <tr>
                        <td className="empty-cell">此使用者尚未掛載任何角色</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="table-card">
                  <table>
                    <thead>
                      <tr>
                        <th>角色 key</th>
                        <th>顯示名稱</th>
                        <th>2FA</th>
                      </tr>
                    </thead>
                    <tbody>
                      {user.roles.map((r) => (
                        <tr key={r.id}>
                          <td style={{ fontFamily: 'monospace', fontSize: 12 }}>
                            <Link to={`/admin/roles/${r.id}`}>{r.key}</Link>
                          </td>
                          <td>{r.display_name}</td>
                          <td>
                            {r.require_2fa ? (
                              <Pill tone="warning">強制 2FA</Pill>
                            ) : (
                              <Pill tone="neutral">免</Pill>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}
